import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars, FaTimes } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const Header = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();

  // ensure language state is in sync with localStorage / i18next
  const initialLang = localStorage.getItem("lang") || i18n.language || "en";
  useEffect(() => {
    if (i18n.language !== initialLang) i18n.changeLanguage(initialLang);
    // apply dir and html lang and body class on mount
    document.documentElement.lang = initialLang;
    document.documentElement.dir = initialLang === "ur" ? "rtl" : "ltr";
    if (initialLang === "ur") {
      document.body.classList.add("font-ur");
    } else {
      document.body.classList.remove("font-ur");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  // nav items keep same paths; display text will use translations when available
  const navItems = [
      { name: "Home", path: "/", key: "nav.home" },
      { name: "About Us", path: "/about", key: "nav.about" },
      { name: "Services", path: "/services", key: "nav.services" },
      { name: "Umrah & Tickets", path: "/umrahtickets", key: "nav.umrahtickets" }, // 👈 ADD THIS LINE
      { name: "Contact", path: "/contact", key: "nav.contact" },
      { name: "FAQs", path: "/faqs", key: "nav.faqs" },
    ];

  // change language helper (updates i18n, html lang/dir, localStorage, and body class)
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ur" ? "rtl" : "ltr";

    if (lang === "ur") {
      document.body.classList.add("font-ur");
    } else {
      document.body.classList.remove("font-ur");
    }
  };

  return (
          <header className="fixed top-0 left-0 w-full z-50 bg-black/90 backdrop-blur-md border-b border-gray-800">
            <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
              {/* Brand Name */}
            <Link
        to="/"
        onClick={closeMenu}
        className="flex items-center gap-2"
      >
        <img
          src="/logo.png"
          alt="Rawangi Travels"
          className="h-10 w-10 object-contain"
        />

        <span className="text-xl font-extrabold tracking-wider bg-gradient-to-r from-blue-400 to-blue-600 bg-clip-text text-transparent">
          Rawangi Travels
        </span>
      </Link> 

        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-8 text-sm font-medium items-center">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={closeMenu}
              className={`transition duration-300 hover:text-blue-400 ${
                location.pathname === item.path ? "text-blue-400" : "text-gray-300"
              }`}
            >
              {/* use translation key if available, otherwise fallback to original name */}
              {t(item.key, item.name)}
            </Link>
          ))}

          {/* Language Switcher (Desktop) */}
          <div className="ml-4 flex items-center gap-2">
            <button
              onClick={() => changeLanguage("en")}
              className={`px-2 py-1 rounded ${
                i18n.language === "en" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"
              }`}
              aria-label="Switch to English"
            >
              EN
            </button>

            <button
              onClick={() => changeLanguage("ur")}
              className={`px-2 py-1 rounded font-[inherit] ${
                i18n.language === "ur" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"
              }`}
              aria-label="اردو میں دیکھیں"
            >
              اردو
            </button>
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-3">
          {/* Language Switcher (Mobile visible on md:hidden if you want; but keep accessible) */}
          <div className="hidden md:block" />
          <div className="md:hidden flex gap-2 mr-2">
            <button
              onClick={() => changeLanguage("en")}
              className={`px-2 py-1 rounded ${
                i18n.language === "en" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"
              }`}
              aria-label="Switch to English"
            >
              EN
            </button>
            <button
              onClick={() => changeLanguage("ur")}
              className={`px-2 py-1 rounded ${
                i18n.language === "ur" ? "bg-blue-600 text-white" : "bg-gray-800 text-gray-300"
              }`}
              aria-label="Switch to Urdu"
            >
              اردو
            </button>
          </div>

          <button
            onClick={toggleMenu}
            className="md:hidden text-gray-300 focus:outline-none hover:text-blue-400 transition"
            aria-label="Toggle menu"
          >
            {isOpen ? <FaTimes size={22} /> : <FaBars size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="md:hidden bg-black/95 backdrop-blur-sm border-t border-gray-800">
          <nav className="flex flex-col items-center space-y-4 py-6">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                onClick={closeMenu}
                className={`text-lg transition hover:text-blue-400 ${
                  location.pathname === item.path ? "text-blue-400" : "text-gray-300"
                }`}
              >
                {t(item.key, item.name)}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
